import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const CART_KEY = "kg_cart";
const API = process.env.REACT_APP_BACKEND_URL;

export async function fetchPaymentMethods() {
  try {
    const res = await fetch(`${API}/api/payments/methods`);
    return res.ok ? await res.json() : { paypal: false };
  } catch {
    return { paypal: false };
  }
}

export async function startCheckout(items, promoCode) {
  const res = await fetch(`${API}/api/payments/paypal/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, origin_url: window.location.origin, promo_code: promoCode || null }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "checkout failed");
  }
  const data = await res.json();
  window.location.href = data.checkout_url;
}

export async function startSubscription(lookupKey) {
  const res = await fetch(`${API}/api/payments/paypal/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lookup_key: lookupKey, origin_url: window.location.origin }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "subscription failed");
  }
  const data = await res.json();
  window.location.href = data.checkout_url;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.lookupKey === product.lookupKey);
      if (existing) {
        return prev.map((i) => (i.lookupKey === product.lookupKey ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((lookupKey, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.lookupKey === lookupKey ? { ...i, qty: Math.max(0, i.qty + delta) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback((lookupKey) => setItems((prev) => prev.filter((i) => i.lookupKey !== lookupKey)), []);
  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({
    items, addItem, updateQty, removeItem, clearCart,
    count: items.reduce((sum, i) => sum + i.qty, 0),
    subtotal: items.reduce((sum, i) => sum + i.price * i.qty, 0),
    drawerOpen, setDrawerOpen,
  }), [items, drawerOpen, addItem, updateQty, removeItem, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
